import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { ZiggyVue } from 'ziggy-js';
import components from './components';
import {useUserStore} from "./domain/user/stores/useUserStore";

/**
 * Global Pinia Instance
 * We initialize this once so all independent "Islands" (Vue apps)
 * share the same central data store and reactive state.
 */
const pinia = createPinia();

/**
 * Safely parses JSON strings from HTML attributes.
 * * @param {string|null} str - The raw JSON string from el.dataset
 * @param {Object} [fallback={}] - What to return if parsing fails
 * @returns {Object} The parsed object or the fallback
 */
const safeParse = (str, fallback = {}) => {
    if (!str) return fallback;
    try {
        return JSON.parse(str);
    } catch (e) {
        console.error(`[Vue Registry] JSON Parse Error: Check your Blade syntax.`, { input: str, error: e });
        return fallback;
    }
};


/**
 * THE MOUNTING ENGINE
 * * Scans the DOM for [data-vue] attributes and mounts them as standalone Vue apps.
 * This allows us to use Vue components inside legacy Blade files without
 * taking over the entire page or breaking existing jQuery listeners.
 * * @example
 * <div data-vue="MyComponent" data-user-id="1" data-json-props='@json($data)'></div>
 */
const mountWidgets = async () => {
    /** @type {NodeListOf<HTMLElement>} */
    const widgets = document.querySelectorAll('[data-vue]');
    if (widgets.length === 0) return;

    // Initialize the User Store ONCE for the whole page
    const userStore = useUserStore(pinia);
    if (!userStore.isLoaded) {
        await userStore.fetchUser();
    }

    widgets.forEach(el => {
        // Prevent re-mounting if this function is called multiple times (e.g., after AJAX)
        if (el.__vue_app__) return;

        const name = el.dataset.vue;
        const Component = components[name];

        if (!Component) {
            console.warn(`[Vue] Component "${name}" not found in registry.`);
            return;
        }

        /**
         * PROP RESOLUTION STRATEGY
         * 1. Extract all 'data-' attributes into a 'props' object.
         * 2. Browser auto-converts kebab-case (data-user-id) to camelCase (userId).
         */
        let props = { ...el.dataset };

        /**
         * COMPLEX DATA HANDLING
         * If 'data-json-props' is present, parse it and merge it into the props object.
         * This allows passing arrays/objects from PHP to Vue.
         */
        if (el.dataset.jsonProps) {
            const extraProps = safeParse(el.dataset.jsonProps);
            props = { ...props, ...extraProps };
        }

        /** * CLEANUP
         * Remove internal mounting metadata so the Vue component only
         * receives actual data props.
         */
        delete props.vue;
        delete props.jsonProps;

        try {
            const app = createApp(Component, props);
            app.use(pinia);
            app.use(ZiggyVue);
            app.mount(el);

            // Store the app instance on the DOM element for debugging/tracking/prevent double init
            el.__vue_app__ = app;
        } catch (err) {
            console.error(`[Vue Registry] Failed to mount island "${name}":`, err);
        }
    });
};

document.addEventListener('DOMContentLoaded', mountWidgets);

/**
 * Global Export
 * If a legacy jQuery script injects new HTML via AJAX, call `window.mountWidgets()`
 * in the AJAX success callback to initialize any new Vue islands in the response.
 */
window.mountWidgets = mountWidgets;